import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { NavLink, useRouteMatch } from 'react-router-dom';
import { AppBar, Box, Button, Icon, IconButton, Menu, MenuItem, Typography } from '@popmenu/common-ui';
import { Menu as MenuIcon } from '@popmenu/web-icons';
import { classNames, makeStyles } from '../../../utils/withStyles';
import { screenReaderOnly } from '../../../assets/jss/utils/screenReaderOnly';
import PopmenuLogoSVG from '../../../assets/svg/popmenu_logo_2020.svg';
import { useEatSession } from '../../hooks/useEatSession';
import { useRefinePath } from '../../hooks/useRefinePath';
import { useSignInPath } from '../../hooks/useSignInPath';
import { useSignOut } from '../../hooks/useSignOut';
import { NavAccountMenu } from '../NavAccountMenu';

const useStyles = makeStyles(theme => ({
  appBar: {
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2),
    },
    backgroundColor: theme.palette.surface.light,
    borderBottom: `1px solid ${theme.palette.gray.main}`,
    padding: theme.spacing(2, 4),
  },
  inner: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    margin: '0 auto',
    maxWidth: '1200px',
  },
  logo: {
    [theme.breakpoints.down('md')]: {
      height: theme.spacing(3),
      top: '5px',
    },
    height: theme.spacing(5),
    position: 'relative',
    top: theme.spacing(1),
  },
  logoText: {
    ...screenReaderOnly,
  },
  menuButton: {
    fontSize: '1rem',
    padding: theme.spacing(1),
  },
  menuButtonContainer: {
    [theme.breakpoints.down('md')]: {
      display: 'block',
    },
    display: 'none',
  },
  navLink: {
    '&.active': {
      color: theme.palette.red.main,
    },
  },
  navLinksContainer: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

// Shares the nav's height as --eat-nav-height, so pages can stick headers just below it and size themselves to the
// rest of the screen
const useNavHeightVariable = (navRef: React.RefObject<HTMLElement>) => {
  useEffect(() => {
    const nav = navRef.current;
    if (!nav || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--eat-nav-height', `${nav.offsetHeight}px`);
    });
    observer.observe(nav);
    return () => observer.disconnect();
  }, [navRef]);
};

export const Nav = () => {
  const classes = useStyles();
  const intl = useIntl();
  const navRef = useRef<HTMLElement>(null);
  useNavHeightVariable(navRef);
  const dishFeedActive = useRouteMatch('/places');
  const { loading: sessionLoading, user } = useEatSession();
  const refinePath = useRefinePath();
  const signInPath = useSignInPath();
  const signOut = useSignOut();

  const [navAnchorEl, setNavAnchorEl] = useState<HTMLButtonElement | null>(null);
  const onNavButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setNavAnchorEl(e.currentTarget);
  };
  const onNavClose = () => {
    setNavAnchorEl(null);
  };
  const onSignOut = () => {
    onNavClose();
    void signOut();
  };

  return (
    <React.Fragment>
      <AppBar
        ref={navRef}
        className={classes.appBar}
        color="secondary"
        component="nav"
        position="sticky"
      >
        <Box>
          <Box className={classes.inner}>
            <NavLink to="/">
              <Typography component="span">
                <PopmenuLogoSVG aria-hidden className={classes.logo} />
                <span className={classes.logoText}>
                  <FormattedMessage defaultMessage="Popmenu" />
                </span>
              </Typography>
            </NavLink>
            <Box className={classes.navLinksContainer}>
              <Button
                className={classNames(
                  classes.navLink,
                  dishFeedActive ? 'active' : null,
                )}
                component={NavLink}
                exact
                role="link"
                to="/"
                variant="text"
              >
                <FormattedMessage defaultMessage="Dish Feed" />
              </Button>
              <NavAccountMenu />
            </Box>
            <Box className={classes.menuButtonContainer}>
              <IconButton
                aria-controls="nav-menu"
                aria-haspopup="true"
                aria-label={intl.formatMessage({ defaultMessage: 'Open navigation menu' })}
                className={classes.menuButton}
                onClick={onNavButtonClick}
              >
                <Icon icon={MenuIcon} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </AppBar>
      <Menu
        anchorEl={navAnchorEl}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        id="nav-menu"
        keepMounted
        onClose={onNavClose}
        open={Boolean(navAnchorEl)}
      >
        <MenuItem
          className={classNames(
            classes.navLink,
            dishFeedActive ? 'active' : null,
          )}
          // @ts-expect-error No overload matches this call.
          component={NavLink}
          exact
          onClick={onNavClose}
          role="link"
          to="/"
          variant="text"
        >
          <FormattedMessage defaultMessage="Dish Feed" />
        </MenuItem>
        {/* Menu children can't be fragments, so the signed-in items are an array */}
        {!sessionLoading && (user ? [
          <MenuItem
            key="profile"
            component={NavLink}
            onClick={onNavClose}
            role="link"
            to="/profile"
          >
            <FormattedMessage defaultMessage="Your taste profile" />
          </MenuItem>,
          refinePath && (
            <MenuItem
              key="refine"
              component={NavLink}
              onClick={onNavClose}
              role="link"
              to={refinePath}
            >
              <FormattedMessage defaultMessage="Refine your palate" />
            </MenuItem>
          ),
          <MenuItem key="sign-out" onClick={onSignOut}>
            <FormattedMessage defaultMessage="Sign out" />
          </MenuItem>,
        ] : (
          <MenuItem
            component={NavLink}
            onClick={onNavClose}
            role="link"
            to={signInPath}
          >
            <FormattedMessage defaultMessage="Sign in" />
          </MenuItem>
        ))}
      </Menu>
    </React.Fragment>
  );
};
